
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardType, Namespace } from 'enums';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { basicSEO } from 'helpers/jsx';
import { useNotificationContext } from 'context';
import { getSharedApiClient } from 'api/client';
import Cards from 'components/Cards';
import Layout from 'components/Layout';
import { useAuth, withForcePlanToLoggedInUnsubs } from 'context/authContext';
import { ProvideStripe, useStripeCtx } from 'context/stripeContext';
import {
  createFormSchema,
  createRedirectIfNeeded,
  emailRule,
  notEmptyRule,
  Permalink,
  resetForm,
  setLoadingState,
  validateForm,
} from 'helpers';
import { Plan } from 'typings/api';
import styles from './index.module.scss';
import Analytics, { SEZNAM_CID_SUCCESS_VOUCHER } from 'helpers/Analytics';
import classNames from 'classnames';
import { StaticPathParams } from 'typings/staticPaths';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { useLang } from 'context/langContext';
import { host } from '../../helpers/env';
import StripeDisclaimer from 'components/StripeDisclaimer';
import ReCAPTCHA from 'react-google-recaptcha-enterprise';
import { Country } from 'enums/country';
import Trans from 'next-translate/Trans';
import { rewritePricesForDiscountedPlans } from 'helpers/plans';

interface Props {
  plans: Plan[];
  countryCode: string;
}

const BuyVoucher = ({ plans, countryCode }: Props): JSX.Element => {
  const formRef = useRef(null);
  const { t } = useTranslation(Namespace.Vouchers);
  const { createNotification } = useNotificationContext();
  const [selectedCard, setSelectedCard] = useState('');
  const [recaptchaError, setRecaptchaError] = useState<string>();
  const [recaptchaToken, setRecaptchaToken] = useState<string>(undefined);
  const refMailto = useRef<HTMLAnchorElement>();

  console.log(plans);

  const { user, isUserLoaded } = useAuth();
  const router = useRouter();
  const { locale } = useLang();

  const { createPaymentMethod, cardHasError, confirmCardPayment, stripeCard, stripeError } = useStripeCtx();

  const RULE_NOT_EMPTY = t('common:form.not_empty');
  const RULE_EMAIL = t('common:form.email_format');

  const setServerError = useCallback(
    (msg: string) => {
      stripeCard.current.classList.add('error');
      stripeError.current.innerHTML = msg;
    },
    [stripeCard, stripeError],
  );

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const form = event.target as HTMLFormElement;
      const planId = parseInt(form.plan.value);
      const plan = plans.find((p) => p.id === planId);

      // buy voucher result callback OK
      const voucherBuySuccessCallback = () => {
        createNotification({ title: t('buy_success') });
        Analytics.v2_voucherPurchase(plan);
        router.push(Permalink.root(null, locale, `seznam_cId=${SEZNAM_CID_SUCCESS_VOUCHER}`));
      };

      // buy voucher result callback NONOK
      const voucherBuyFailedCallback = () => {
        createNotification({ title: t('buy_fail') });
        resetForm(formRef.current);
      };

      if (!user) {
        const formSchema = createFormSchema({
          email: emailRule(RULE_EMAIL, RULE_NOT_EMPTY),
          taxCountry: notEmptyRule(RULE_NOT_EMPTY),
        });
        const email = form.email?.value;
        const taxCountry = form.taxCountry?.value;
        const validateObject = {
          email,
          taxCountry,
        };

        if (!recaptchaToken) {
          setRecaptchaError(t('common:form.recaptcha_error_not_checked'));
        }

        const valid = validateForm(formSchema, validateObject, formRef.current) && recaptchaToken;
        if (valid && !cardHasError) {
          const payment = await createPaymentMethod();
          if (payment.error) {
            resetForm(formRef.current);
            setServerError(payment.error.message);
          } else {
            try {
              const res = await getSharedApiClient().paymentVoucher({
                email,
                payment_method: payment.paymentMethod.id,
                plan_id: planId,
                currency: plan.price.currency,
                tax_country: +taxCountry,
                g_recaptcha: recaptchaToken,
              });

              if (res.status === 'ACTION_REQUIRED') {
                const confirmResp = await confirmCardPayment(
                  res.payment_intent_client_secret,
                  null,
                  payment.paymentMethod.id,
                );
                if (confirmResp.error) {
                  setServerError(confirmResp.error.message);
                  resetForm(formRef.current);
                } else {
                  voucherBuySuccessCallback();
                }
              } else if (res.status === 'SUCCEEDED') {
                voucherBuySuccessCallback();
              } else {
                voucherBuyFailedCallback();
              }
            } catch (e) {
              voucherBuyFailedCallback();
            }
          }
        } else {
          resetForm(formRef.current);
        }
      } else {
        setLoadingState(formRef.current);
        let payMethod: string;
        if (selectedCard !== CardType.NewCard && selectedCard !== '') {
          // user choose saved card, try pay with it
          payMethod = selectedCard;
        } else {
          if (!cardHasError) {
            const respPay = await createPaymentMethod();
            if (respPay.error) {
              setServerError(respPay.error.message);
              resetForm(formRef.current);
            } else {
              payMethod = respPay.paymentMethod.id;
            }
          }
        }
        try {
          const res = await getSharedApiClient().paymentVoucher({
            payment_method: payMethod,
            currency: plan.price.currency,
            plan_id: planId,
          });
          // TODO: mrkni na /payments/continue
          if (res.status === 'ACTION_REQUIRED') {
            const confirmResp = await confirmCardPayment(res.payment_intent_client_secret, null, payMethod);
            if (confirmResp.error) {
              setServerError(confirmResp.error.message);
              resetForm(formRef.current);
            } else {
              voucherBuySuccessCallback();
            }
          } else {
            voucherBuySuccessCallback();
          }
        } catch (e) {
          voucherBuyFailedCallback();
        }
      }
    },
    [
      formRef,
      cardHasError,
      selectedCard,
      confirmCardPayment,
      createPaymentMethod,
      stripeCard,
      stripeError,
      recaptchaError,
      recaptchaToken,
    ],
  );

  useEffect(() => {
    if (recaptchaToken) {
      setRecaptchaError('');
    }
  }, [recaptchaToken]);

  useEffect(() => {
    if (refMailto.current) {
      refMailto.current.href = `mailto:${refMailto.current.text}`;
    }
  }, [refMailto]);

  const onRecaptchaError = useCallback(() => {
    setRecaptchaError(t('common:form.recaptcha_error_global'));
  }, []);

  const onRecaptchaExpired = useCallback(() => {
    setRecaptchaError(t('common:form.recaptcha_error_expired'));
    setRecaptchaToken(undefined);
  }, []);

  const isCountryCzechia = useMemo(() => {
    return isUserLoaded && countryCode === Country.Czechia;
  }, [isUserLoaded, user, countryCode]);

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:vouchers_buy_title'), t('seo:vouchers_buy_description'))}</Head>
      <main className={styles.vouchers}>
        <div className={styles.donateWrapper}>
          <h1>{t('title')}</h1>
          <p>{t('description')}</p>
          <form ref={formRef} onSubmit={onSubmit}>
            <div className={classNames('radios', styles.radios)}>
              {plans?.map((plan) => {
                return (
                  <Fragment key={plan.id}>
                    <input
                      type="radio"
                      defaultChecked={plan.recurrence.length === 1 && plan.recurrence.unit === 'MONTH'}
                      name="plan"
                      value={plan.id}
                      id={`${plan.id}`}
                    />
                    <label className={classNames('radio', styles.radio)} htmlFor={`${plan.id}`}>
                      <strong>{plan.alias}</strong>
                      {t('vouchers:price_with_vat', { price: plan.price.formatted })}
                    </label>
                  </Fragment>
                );
              })}
            </div>
            <Cards selected={selectedCard} setSelected={setSelectedCard} showCountrySelect />
            {isUserLoaded && !user && (
              <div className={styles.recaptcha}>
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE}
                  onChange={setRecaptchaToken}
                  onErrored={onRecaptchaError}
                  onExpired={onRecaptchaExpired}
                  hl={locale}
                />
                {recaptchaError && <span className="standalone-error">{recaptchaError}</span>}
              </div>
            )}
            <button type="submit" className="btn btn-primary">
              {t('buy_btn')}
            </button>
            {isCountryCzechia && <StripeDisclaimer />}
            {isCountryCzechia && (
              <>
                <div className={styles.divider}>
                  <span>{t('common:or')}</span>
                </div>
                <a
                  rel="external"
                  target="_blank"
                  href="https://www.online.dramox.cz/product-page/dramox-d%C3%A1rkov%C3%BD-voucher"
                  className="btn btn-warning"
                >
                  {t('buy_physical_voucher')}
                </a>
              </>
            )}
            <p className="companies">
              <Trans
                i18nKey={'vouchers:for_companies'}
                components={{
                  contactMail: <a href="#" ref={refMailto} />,
                }}
              />
            </p>
          </form>
          {isCountryCzechia && (
            <a className={styles.givery} target="_blank" href="https://www.givery.cz/dramox/">
              {t('givery')}
            </a>
          )}
        </div>
      </main>
    </Layout>
  );
};
 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { apiClient, locale, countryCode } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  const allPlans = await apiClient.getPlans();
  let plans = allPlans.filter((p) => p.is_public && p.is_voucher_compatible && p.price);

  plans = rewritePricesForDiscountedPlans(plans, 'voucher-compatible');

  return {
    props: { plans, countryCode },
  };
}

export default ProvideStripe(BuyVoucher);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/gift-vouchers/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  